import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Mic, Square, Send, Trash2, Check, RotateCcw, Phone } from 'lucide-react'
import { api, ApiError } from '../../lib/api.js'
import { toast } from '../../components/feedback/Toast.jsx'
import { useOnline } from '../../lib/useOnline.js'
import { fmtDateTime } from '../../lib/date.js'
import { cn } from '../../lib/cn.js'

/**
 * Phone Log (Charles, 2026-09-11): "a way to very quickly log a phone call
 * that came in with minimum interference." One card: a text box, a voice
 * note that transcribes itself while it records, and Send — which saves
 * the entry and emails it to bookings@. No caller, no subject, no date
 * field; the moment it was saved is the only metadata.
 *
 * The transcript comes from the browser's own speech recognition (Apple's
 * on iPhone, Google's in Chrome — free, no service of ours), running
 * beside MediaRecorder. Once the words read right the recording can be
 * thrown away; a browser without speech recognition still records.
 */

const SR = typeof window !== 'undefined' ? window.SpeechRecognition || window.webkitSpeechRecognition : null
const canRecord = typeof MediaRecorder !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia)
// iPhone records audio/mp4; Chrome and Firefox record webm/ogg. The server
// names the file from whichever this turns out to be.
const MIME = canRecord
  ? ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/ogg'].find(
      (m) => MediaRecorder.isTypeSupported?.(m)
    )
  : undefined

const fmtElapsed = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`

/** The recorder + live transcriber, as one state machine: idle → recording → done. */
function useVoiceNote() {
  const [state, setState] = useState('idle')
  const [elapsed, setElapsed] = useState(0)
  const [transcript, setTranscript] = useState('') // final words so far (editable once done)
  const [interim, setInterim] = useState('')       // what the recognizer is still deciding on
  const [audio, setAudio] = useState(null)          // {blob, url} once stopped, null if discarded
  const [interrupted, setInterrupted] = useState(false) // the mic track died mid-recording
  const [srLive, setSrLive] = useState(false)       // recognition actually delivering
  const rec = useRef(null)
  const sr = useRef(null)
  const stream = useRef(null)
  const chunks = useRef([])
  const timer = useRef(null)
  const committed = useRef('') // finals from recognizer sessions that already ended
  const stopping = useRef(false)

  useEffect(() => () => teardown(), []) // leaving the page stops everything

  function teardown() {
    clearInterval(timer.current)
    try { sr.current?.abort() } catch { /* already gone */ }
    try { if (rec.current?.state !== 'inactive') rec.current?.stop() } catch { /* already gone */ }
    stream.current?.getTracks().forEach((t) => t.stop())
    sr.current = null
    rec.current = null
    stream.current = null
  }

  function startRecognizer() {
    if (!SR) return
    const r = new SR()
    r.continuous = true
    r.interimResults = true
    r.lang = navigator.language?.startsWith('en') ? navigator.language : 'en-US'
    let sessionFinal = ''
    r.onresult = (e) => {
      // Rebuild from the whole result list each time: iOS re-sends earlier
      // results as they firm up, so appending would double the words.
      let finals = '', pending = ''
      for (let i = 0; i < e.results.length; i++) {
        const t = e.results[i][0]?.transcript || ''
        if (e.results[i].isFinal) finals += t + ' '
        else pending += t
      }
      sessionFinal = finals
      setSrLive(true)
      setTranscript((committed.current + sessionFinal).replace(/\s+/g, ' ').trim())
      setInterim(pending.trim())
    }
    r.onerror = (e) => {
      // Denied or unsupported: stay quiet and just record. Anything else
      // (a silence timeout, a network blip) is followed by onend, which
      // restarts it while we're still recording.
      if (['not-allowed', 'service-not-allowed', 'audio-capture'].includes(e.error)) sr.current = null
    }
    r.onend = () => {
      committed.current = (committed.current + sessionFinal).replace(/\s+/g, ' ')
      if (committed.current && !committed.current.endsWith(' ')) committed.current += ' '
      sessionFinal = ''
      setInterim('')
      if (stopping.current || sr.current !== r) return
      // Recognizers stop themselves after a pause (iPhone does it every
      // minute or so) — keep listening for as long as the recording runs.
      try { r.start() } catch { sr.current = null }
    }
    sr.current = r
    try { r.start() } catch { sr.current = null }
  }

  async function start() {
    if (state === 'recording') return
    stopping.current = false
    committed.current = ''
    chunks.current = []
    setTranscript('')
    setInterim('')
    setInterrupted(false)
    setSrLive(false)
    setElapsed(0)
    if (audio) URL.revokeObjectURL(audio.url)
    setAudio(null)
    // Recognition first, synchronously — it needs the tap that started
    // it, and Safari's leash on that is short. The mic prompt can await.
    startRecognizer()
    if (!canRecord) {
      if (!sr.current) { toast('This browser can’t record or transcribe — type the note instead', 'error'); return }
      setState('recording')
      timer.current = setInterval(() => setElapsed((s) => s + 1), 1000)
      return
    }
    try {
      stream.current = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch {
      try { sr.current?.abort() } catch { /* fine */ }
      sr.current = null
      toast('Microphone access was refused — type the note instead', 'error')
      return
    }
    const track = stream.current.getAudioTracks()[0]
    if (track) track.onended = () => setInterrupted(true)
    const r = new MediaRecorder(stream.current, MIME ? { mimeType: MIME } : undefined)
    r.ondataavailable = (e) => { if (e.data?.size) chunks.current.push(e.data) }
    r.onstop = () => {
      const blob = new Blob(chunks.current, { type: r.mimeType || MIME || 'audio/mp4' })
      setAudio(blob.size ? { blob, url: URL.createObjectURL(blob) } : null)
      stream.current?.getTracks().forEach((t) => t.stop())
      stream.current = null
      setState('done')
    }
    rec.current = r
    r.start(1000) // a chunk a second, so a crash mid-call still leaves most of it
    setState('recording')
    timer.current = setInterval(() => setElapsed((s) => s + 1), 1000)
  }

  function stop() {
    if (state !== 'recording') return
    stopping.current = true
    clearInterval(timer.current)
    try { sr.current?.stop() } catch { /* fine */ }
    if (rec.current && rec.current.state !== 'inactive') rec.current.stop() // onstop → done
    else setState('done')
    rec.current = null
  }

  function discardAudio() {
    if (audio) URL.revokeObjectURL(audio.url)
    setAudio(null)
  }

  function reset() {
    teardown()
    discardAudio()
    committed.current = ''
    setTranscript('')
    setInterim('')
    setElapsed(0)
    setInterrupted(false)
    setSrLive(false)
    setState('idle')
  }

  return { state, elapsed, transcript, setTranscript, interim, audio, interrupted, srLive, start, stop, discardAudio, reset }
}

async function postCall({ text, transcript, audio }) {
  const form = new FormData()
  form.append('text', text)
  form.append('transcript', transcript)
  if (audio) form.append('audio', audio.blob, 'voice-note')
  const res = await fetch('/api/phone', { method: 'POST', body: form, credentials: 'same-origin' })
  if (res.status === 401) {
    window.dispatchEvent(new Event('cdm:unauthorized'))
    throw new ApiError('unauthorized', 401)
  }
  const data = await res.json().catch(() => null)
  if (!res.ok) throw new ApiError(data?.error || 'couldn’t save', res.status, data)
  return data
}

export default function PhoneLogPage() {
  const qc = useQueryClient()
  const online = useOnline()
  const { data, isLoading } = useQuery({ queryKey: ['phone'], queryFn: () => api('/phone') })
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const voice = useVoiceNote()
  const to = data?.to || 'bookings@cdmstudios.com'

  const hasSomething = Boolean(text.trim() || voice.transcript.trim() || voice.audio)
  const canSend = online && hasSomething && !busy && voice.state !== 'recording'

  async function send() {
    if (!canSend) return
    setBusy(true)
    try {
      const saved = await postCall({ text: text.trim(), transcript: voice.transcript.trim(), audio: voice.audio })
      if (saved.emailed) toast(`Logged and emailed to ${saved.to}`, 'success')
      else toast(`Logged — but the email didn’t go (${saved.emailError || 'mail not set up'}). It’s saved below.`, 'error')
      setText('')
      voice.reset()
      qc.invalidateQueries({ queryKey: ['phone'] })
    } catch (err) {
      toast(err.message || 'Couldn’t save the call', 'error')
    } finally {
      setBusy(false)
    }
  }

  async function resend(item) {
    try {
      const r = await api(`/phone/${item.id}/resend`, { method: 'POST' })
      toast(r.emailed ? `Emailed to ${r.to}` : `Still couldn’t email: ${r.emailError}`, r.emailed ? 'success' : 'error')
      qc.invalidateQueries({ queryKey: ['phone'] })
    } catch (err) {
      toast(err.message, 'error')
    }
  }

  async function remove(item) {
    if (!window.confirm('Delete this call log?')) return
    try {
      await api(`/phone/${item.id}`, { method: 'DELETE' })
      qc.invalidateQueries({ queryKey: ['phone'] })
    } catch (err) {
      toast(err.message, 'error')
    }
  }

  async function dropAudio(item) {
    try {
      await api(`/phone/${item.id}/audio`, { method: 'DELETE' })
      qc.invalidateQueries({ queryKey: ['phone'] })
    } catch (err) {
      toast(err.message, 'error')
    }
  }

  const recording = voice.state === 'recording'
  const done = voice.state === 'done'

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-4 border-b border-hairline pb-3">
        <h1 className="text-2xl font-semibold text-ink">Phone Log</h1>
        <p className="mt-1 text-sm text-muted">A call came in? Type it or say it — it goes straight to {to}.</p>
      </div>

      {online ? (
        <div className="card mb-6 p-3">
          <textarea
            rows={3}
            placeholder="What was the call about?"
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="w-full resize-y rounded-lg border-0 bg-grouped px-3 py-2.5 text-[16px] placeholder:text-faint focus:bg-white focus:outline-none focus:ring-2 focus:ring-link"
          />

          {/* ── Voice note ── */}
          <div className={cn('mt-3 rounded-lg border', recording ? 'border-danger/60 bg-white' : 'border-hairline bg-grouped/60')}>
            {voice.state === 'idle' && (
              <button
                onClick={voice.start}
                className="tap flex w-full items-center justify-center gap-2 rounded-lg px-3 py-3 text-[15px] font-medium text-navy"
              >
                <Mic size={18} /> Record a voice note
                {!SR && <span className="text-xs font-normal text-faint">(no live transcript in this browser)</span>}
              </button>
            )}

            {recording && (
              <div className="p-3">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-sm font-medium text-danger">
                    <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-danger" /> Recording · {fmtElapsed(voice.elapsed)}
                  </span>
                  <button
                    onClick={voice.stop}
                    className="tap flex items-center gap-1.5 rounded-xl bg-ink px-3.5 py-2 text-sm font-medium text-white"
                  >
                    <Square size={13} fill="currentColor" /> Stop
                  </button>
                </div>
                <p className="mt-3 min-h-[3rem] text-[16px] leading-relaxed text-ink" aria-live="polite">
                  {voice.transcript}
                  {voice.interim && <span className="text-faint"> {voice.interim}</span>}
                  {!voice.transcript && !voice.interim && (
                    <span className="text-faint">{SR ? 'Listening… your words appear here as you speak.' : 'Recording. Type the note above — this browser can’t transcribe.'}</span>
                  )}
                </p>
              </div>
            )}

            {done && (
              <div className="p-3">
                {voice.interrupted && (
                  <p className="mb-2 text-xs text-danger">The microphone cut out while recording — check the audio before sending.</p>
                )}
                {voice.audio ? (
                  <div className="flex items-center gap-2">
                    <audio controls src={voice.audio.url} className="h-9 min-w-0 flex-1" preload="metadata" />
                  </div>
                ) : (
                  <p className="text-sm text-muted">Recording deleted — the transcript below is what gets sent.</p>
                )}
                <label className="mt-3 block text-xs font-medium text-muted">
                  Transcript{SR ? ' — fix anything it misheard' : ''}
                </label>
                <textarea
                  rows={3}
                  value={voice.transcript}
                  onChange={(e) => voice.setTranscript(e.target.value)}
                  placeholder={SR ? 'Nothing was picked up. Type what was said, or record again.' : 'Type what was said.'}
                  className="mt-1 w-full resize-y rounded-lg border-0 bg-white px-3 py-2.5 text-[16px] ring-1 ring-hairline placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-link"
                />
                <div className="mt-2 flex items-center justify-between text-sm">
                  {voice.audio ? (
                    <button onClick={voice.discardAudio} className="tap flex items-center gap-1 text-danger">
                      <Trash2 size={14} /> Delete recording, keep transcript
                    </button>
                  ) : <span />}
                  <button onClick={voice.reset} className="tap flex items-center gap-1 text-muted">
                    <RotateCcw size={14} /> Record again
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="mt-3 flex items-center justify-between">
            <span className="text-xs text-faint">
              {data && !data.mail ? 'Email isn’t set up on this server — entries are saved here only.' : `Sends to ${to}`}
            </span>
            <button
              onClick={send}
              disabled={!canSend}
              className="tap flex items-center gap-1.5 rounded-xl bg-navy px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
            >
              <Send size={14} /> {busy ? 'Sending…' : recording ? 'Stop first' : 'Send'}
            </button>
          </div>
        </div>
      ) : (
        <p className="mb-6 text-sm text-muted">The phone log needs a connection.</p>
      )}

      <div className="section-header mb-2">Recent calls</div>
      {isLoading && <p className="text-sm text-muted">Loading…</p>}
      {data && data.items.length === 0 && <p className="text-sm text-muted">Nothing logged yet.</p>}
      <div className="space-y-2">
        {(data?.items || []).map((item) => (
          <div key={item.id} className="card p-3">
            <div className="flex items-center justify-between text-xs text-muted">
              <span className="flex items-center gap-1.5"><Phone size={12} /> {fmtDateTime(item.createdAt)}{item.by ? ` · ${item.by}` : ''}</span>
              {item.canDelete && (
                <button onClick={() => remove(item)} aria-label="Delete" className="tap -mr-1 rounded-lg p-1 text-faint hover:text-danger">
                  <Trash2 size={14} />
                </button>
              )}
            </div>
            {item.text && <p className="mt-1.5 whitespace-pre-wrap text-[15px] text-ink">{item.text}</p>}
            {item.transcript && (
              <p className="mt-1.5 whitespace-pre-wrap border-l-2 border-hairline pl-2.5 text-[15px] text-ink">
                <span className="text-xs text-muted">Voice note · </span>{item.transcript}
              </p>
            )}
            {item.audio && (
              <div className="mt-2 flex items-center gap-2">
                <audio controls src={item.audio.url} preload="none" className="h-8 min-w-0 flex-1" />
                {item.canDelete && (
                  <button onClick={() => dropAudio(item)} aria-label="Delete recording" className="tap shrink-0 text-xs text-muted">Delete audio</button>
                )}
              </div>
            )}
            <div className="mt-2 flex items-center gap-2 text-xs">
              {item.emailedAt ? (
                <span className="flex items-center gap-1 text-muted"><Check size={12} /> Emailed {fmtDateTime(item.emailedAt)}</span>
              ) : (
                <>
                  <span className="text-muted">Not emailed{item.emailError ? ` — ${item.emailError}` : ''}</span>
                  {(item.mine || item.canDelete) && (
                    <button onClick={() => resend(item)} className="tap font-medium text-link">Resend</button>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
